(function(){
'use strict';

// Pure model for the administrative quote selection. It never writes inventory.
function quantity(value, allowZero = false) {
  const q = Number(value);
  if (!Number.isSafeInteger(q) || q < (allowZero ? 0 : 1) || q > 2147483647) {
    throw new Error('La quantité doit être un nombre entier positif valide.');
  }
  return q;
}

function properties(value) {
  const result = {};
  for (const [key, val] of Object.entries(value || {})) {
    if (['__proto__', 'constructor', 'prototype'].includes(key)) continue;
    if (typeof val !== 'string' && typeof val !== 'number') {
      throw new Error('Une option du produit ne peut pas être conservée.');
    }
    result[key] = String(val);
  }
  return result;
}

function identity(variantId, props) {
  return JSON.stringify([String(variantId), Object.entries(properties(props)).sort(([a], [b]) => a.localeCompare(b))]);
}

function totals(items, attrs = {}, currency = 'EUR') {
  const result = {
    items: items.map(item => {
      const q = quantity(item.quantity);
      const price = Number(item.price);
      if (!Number.isSafeInteger(price) || price < 0 || !Number.isSafeInteger(price * q)) {
        throw new Error('Le prix doit être vérifié avant de créer le devis.');
      }
      return {
        ...item, quantity: q, final_price: price, original_price: price,
        line_price: price * q, final_line_price: price * q, original_line_price: price * q,
        discounts: [], line_level_discount_allocations: []
      };
    }),
    attributes: {...attrs, _discount_genie: 'true', 'Mandat administratif': 'Oui'},
    currency, requires_shipping: true, cart_level_discount_applications: [], total_discount: 0
  };
  result.item_count = result.items.reduce((n, item) => n + item.quantity, 0);
  result.total_price = result.items.reduce((n, item) => n + item.final_line_price, 0);
  result.original_total_price = result.total_price;
  result.items_subtotal_price = result.total_price;
  result.total_weight = result.items.reduce((n, item) => n + Number(item.grams || 0) * item.quantity, 0);
  if (!Number.isSafeInteger(result.total_price) || !Number.isSafeInteger(result.item_count)) {
    throw new Error('La sélection dépasse les limites de calcul du devis.');
  }
  return result;
}

async function add(items, additions, resolveVariant) {
  // Resolve every variant first, then commit all lines together. An unavailable
  // metadata source must not leave half of a personalized product in the basket.
  const incoming = await Promise.all(additions.map(async input => {
    const q = quantity(input.quantity == null ? 1 : input.quantity);
    if (input.selling_plan) throw new Error('Les abonnements nécessitent un devis accompagné.');
    const id = String(input.id);
    if (!/^\d+$/.test(id)) throw new Error('Variante invalide.');
    const variant = await resolveVariant(id);
    if (String(variant.id) !== id) throw new Error('La variante ne correspond pas au produit demandé.');
    const props = properties(input.properties);
    return {
      id: Number(id), variant_id: Number(id), key: identity(id, props),
      title: variant.name, product_title: variant.name,
      variant_title: variant.public_title || variant.title,
      sku: variant.sku || '', price: variant.price, grams: variant.weight || 0,
      quantity: q, properties: props, requires_shipping: variant.requires_shipping !== false,
      taxable: variant.taxable !== false, image: variant.featured_image?.src || null
    };
  }));
  const next = structuredClone(items);
  for (const line of incoming) {
    const existing = next.find(item => item.key === line.key);
    if (existing) existing.quantity = quantity(existing.quantity + line.quantity);
    else next.push(line);
  }
  return totals(next).items;
}

function change(items, key, value) {
  const q = quantity(value, true);
  const target = items.find(item => item.key === key);
  if (!target) throw new Error('Ligne introuvable.');
  const group = target.properties?._apo_order || target.properties?._apo_parent_order;
  if (group) {
    const parents = items.filter(item => item.properties?._apo_order === group);
    if (parents.length !== 1) throw new Error('Le lien de personnalisation doit être vérifié.');
    const parent = parents[0];
    const targetRatio = target === parent ? 1 : quantity(target.properties._apo_quantity);
    const parentQuantity = quantity(q / targetRatio, true);
    return totals(items.map(item => {
      if (item === parent) return {...item, quantity:parentQuantity};
      if (item.properties?._apo_parent_order === group) {
        return {...item, quantity:quantity(parentQuantity * quantity(item.properties._apo_quantity), true)};
      }
      return item;
    }).filter(item => item.quantity > 0)).items;
  }
  return totals(items.map(item => item.key === key ? {...item, quantity: q} : item).filter(item => item.quantity > 0)).items;
}

window.KswAdminBasketCore = {quantity, properties, identity, totals, add, change};

})();
(function () {
  'use strict';
  // Staging implementation: must not be published before the integration tests.
  const seedElement = document.getElementById('ksw-admin-seed');
  if (!seedElement || window.KswAdminBasket) return;
  const seed = JSON.parse(seedElement.textContent);
  const core = window.KswAdminBasketCore;
  const nativeFetch = window.fetch.bind(window);
  const storageKey = 'ksw-administrative-selection-v1';
  let purchaseMode = seed.attributes?._discount_genie === 'true' ? 'admin' : 'personal';
  let attributes = seed.attributes || {};
  let queue = Promise.resolve();
  const base = window.Shopify?.routes?.root || '/';
  function read() {
    const text = localStorage.getItem(storageKey);
    if (!text) return seed.attributes?._discount_genie === 'true' ? (seed.items || []).map(item => ({
      ...item, price:item.original_price ?? item.price,
      key:core.identity(item.variant_id || item.id,item.properties)
    })) : [];
    const saved = JSON.parse(text);
    if (saved.version !== 1 || !Array.isArray(saved.items)) throw new Error('Sélection administrative illisible.');
    return saved.items;
  }
  function write(items) {
    const cart = core.totals(items, attributes, seed.currency || 'EUR');
    localStorage.setItem(storageKey, JSON.stringify({version:1,items:cart.items}));
    document.dispatchEvent(new CustomEvent('ksw:admin-basket', {detail:cart}));
    return cart;
  }
  function snapshot() { return core.totals(read(), attributes, seed.currency || 'EUR'); }
  async function resolveVariant(id) {
    const response = await nativeFetch(base + 'variants/' + encodeURIComponent(id) + '.js', {cache:'no-store'});
    if (!response.ok) throw new Error('Impossible de vérifier le prix de cette variante.');
    return response.json();
  }
  function decode(body) {
    if (body == null) return {};
    if (typeof body === 'string' && body.trim().startsWith('{')) return JSON.parse(body);
    const entries = body instanceof FormData || body instanceof URLSearchParams
      ? body.entries() : new URLSearchParams(body).entries();
    const out = {};
    for (const [key, value] of entries) {
      if (typeof value !== 'string') throw new Error('Un fichier de personnalisation doit être enregistré avant le devis.');
      const parts = key.match(/[^\[\]]+/g) || [];
      if (parts.some(p => ['__proto__','constructor','prototype'].includes(p))) throw new Error('Champ invalide.');
      let cursor = out;
      parts.forEach((part, index) => {
        if (index === parts.length - 1) cursor[part] = value;
        else cursor = cursor[part] ||= /^\d+$/.test(parts[index+1]) ? [] : {};
      });
    }
    return out;
  }
  function operation(url) {
    const parsed = new URL(url, location.origin);
    if (parsed.origin !== location.origin) return null;
    const match = parsed.pathname.match(/\/cart(?:\/(add|change|update|clear))?\.js$/);
    return match ? match[1] || 'read' : null;
  }
  function setMode(mode) {
    purchaseMode = mode;
    document.documentElement.dataset.kswPurchaseMode = mode;
    document.dispatchEvent(new CustomEvent('ksw:purchase-mode', {detail:mode}));
  }
  async function perform(action, data) {
    if (action === 'read') return snapshot();
    const run = async () => {
      let items = read();
      if (action === 'add') {
        const inputs = data.items || [data];
        items = await core.add(items, inputs, resolveVariant);
        write(items);
        const added = inputs.map(input => items.find(item => item.key === core.identity(input.id, input.properties)));
        return data.items ? {items:added} : added[0];
      }
      if (action === 'clear') return write([]);
      if (action === 'change') {
        const item = data.line ? items[Number(data.line)-1] : items.find(row => row.key === data.id || String(row.variant_id) === String(data.id));
        if (!item) throw new Error('Article introuvable dans votre sélection.');
        items = core.change(items, item.key, data.quantity == null ? item.quantity : data.quantity);
        if (data.properties) {
          const updated = items.find(row => row.key === item.key);
          if (updated) {
            updated.properties = core.properties(data.properties);
            updated.key = core.identity(updated.variant_id, updated.properties);
          }
        }
      }
      if (action === 'update' && data.updates) {
        for (const [key, q] of Object.entries(data.updates)) {
          const item = Array.isArray(data.updates) ? items[Number(key)] : items.find(row => row.key === key || String(row.variant_id) === key);
          if (!item) throw new Error('Article introuvable dans votre sélection.');
          items = core.change(items, item.key, q);
        }
      }
      return write(items);
    };
    const locked = () => navigator.locks ? navigator.locks.request(storageKey, run) : run();
    const next = queue.then(locked, locked);
    queue = next.catch(() => {});
    return next;
  }
  async function intercept(input, init) {
    const url = typeof input === 'string' ? input : input.url;
    const action = operation(url);
    if (!action) return nativeFetch(input, init);
    const body = init?.body ?? (input instanceof Request && input.method !== 'GET' ? await input.clone().text() : null);
    const data = decode(body == null ? new URL(url,location.origin).search.slice(1) : body);
    if (data.attributes) {
      // Purchase-mode attributes still belong to the real Shopify cart.
      // Do not silently apply quantity updates to it in administration mode.
      const response = await nativeFetch(base+'cart/update.js', {
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({attributes:data.attributes})
      });
      if (!response.ok) return response;
      const real = await response.json();
      attributes = real.attributes || {};
      setMode(attributes._discount_genie === 'true' ? 'admin' : 'personal');
      if (purchaseMode !== 'admin') return Response.json(real);
    }
    if (purchaseMode !== 'admin') return nativeFetch(input, init);
    try { return Response.json(await perform(action, data)); }
    catch (error) {
      return Response.json({status:422,message:error.message,description:error.message}, {status:422});
    }
  }
  window.fetch = intercept;
  // jQuery and apps using XHR receive the same administrative cart contract.
  const oldOpen = XMLHttpRequest.prototype.open;
  const oldSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function(method, url) {
    this.kswCartRequest = {method,url:String(url)};
    return oldOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function(body) {
    const request = this.kswCartRequest;
    if (!request || !operation(request.url) || purchaseMode !== 'admin') return oldSend.apply(this, arguments);
    const xhr = this;
    intercept(request.url, {method:request.method,body}).then(async response => {
      const text = await response.text();
      const values = {
        readyState:4,status:response.status,statusText:response.statusText,
        responseURL:new URL(request.url,location.origin).href,
        response:xhr.responseType === 'json' ? JSON.parse(text) : text
      };
      if (xhr.responseType !== 'json') values.responseText = text;
      for (const [name,value] of Object.entries(values)) Object.defineProperty(xhr,name,{configurable:true,value});
      xhr.getResponseHeader = name => /content-type/i.test(name) ? 'application/json' : null;
      xhr.getAllResponseHeaders = () => 'content-type: application/json\r\n';
      xhr.dispatchEvent(new Event('readystatechange'));
      xhr.dispatchEvent(new ProgressEvent('load'));
      xhr.dispatchEvent(new ProgressEvent('loadend'));
    }).catch(() => {
      xhr.dispatchEvent(new ProgressEvent('error'));
      xhr.dispatchEvent(new ProgressEvent('loadend'));
    });
  };
  window.KswAdminBasket = {snapshot,change:(key,q)=>perform('change',{id:key,quantity:q}),nativeFetch,setMode};
  setMode(purchaseMode);
})();

(function install() {
  'use strict';
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', install, {once:true});
    return;
  }
  function isAdmin() { return document.documentElement.dataset.kswPurchaseMode === 'admin'; }
  const changed = new Map();
  function update() {
    if (!isAdmin()) {
      for (const [element, state] of changed) {
        if (!element.isConnected) continue;
        element.disabled = state.disabled;
        element.classList.toggle('disable', state.disable);
        if (state.aria == null) element.removeAttribute('aria-label');
        else element.setAttribute('aria-label',state.aria);
      }
      changed.clear();
      return;
    }
    document.querySelectorAll('form[action*="/cart/add"] .btn-addtocart').forEach(button => {
      if (!changed.has(button)) changed.set(button,{disabled:button.disabled,disable:button.classList.contains('disable'),aria:button.getAttribute('aria-label')});
      if (button.getAttribute('aria-label') !== 'Ajouter au devis') button.setAttribute('aria-label','Ajouter au devis');
      // Only lift an inventory rejection. Do not fight the options app when
      // it disables a button while validating fields or preparing a surcharge.
      const soldOut = /épuisé|sold out|indisponible/i.test(button.textContent);
      if (soldOut && button.disabled) button.disabled = false;
      if (soldOut && button.classList.contains('disable')) button.classList.remove('disable');
    });
  }
  document.addEventListener('ksw:purchase-mode', update);
  document.addEventListener('click', function(event) {
    if (!isAdmin()) return;
    const link = event.target.closest('a[href]');
    if (link) {
      const url = new URL(link.href,location.origin);
      if (url.origin === location.origin && /\/cart\/?$/.test(url.pathname)) {
        event.preventDefault(); event.stopImmediatePropagation();
        location.assign('/pages/demande-devis');
      }
    }
    if (event.target.closest('[name="checkout"],#checkout-submit,.km-checkout,a[href*="/checkout"]')) {
      event.preventDefault(); event.stopImmediatePropagation();
      location.assign('/pages/demande-devis');
    }
  }, true);
  document.addEventListener('submit', function(event) {
    if (!isAdmin()) return;
    if (event.target.matches('form[action*="/cart/add"]')) {
      // Non-Ajax fallback only; Ajax buttons retain the app's validation pipeline.
      if (event.defaultPrevented || event.submitter?.classList.contains('addtocart-js')) return;
      event.preventDefault();
      const form = event.target;
      fetch('/cart/add.js',{method:'POST',body:new FormData(form)}).then(async response => {
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Ajout impossible.');
        location.assign('/pages/demande-devis');
      }).catch(error => alert(error.message));
    }
  });
  const observer = new MutationObserver(update);
  observer.observe(document.body,{subtree:true,childList:true});
  update();
  if (isAdmin() && /\/cart\/?$/.test(location.pathname)) location.replace('/pages/demande-devis');
})();
